/**
 * AdoptionAnalytics — Admin → Adoption tab.
 *
 * Full adoption analytics (ported from the standalone SPA): executive Adoption
 * report, usage + loyalty, per-company drill-down, prompt categories, product
 * metrics, and trends/distribution. Data comes from the live backend via
 * AdoptionDataProvider (/api/usage + /api/usage/categorize), not S3 snapshots.
 */
import { useState } from 'react';
import { AdoptionDataProvider, useData } from './adoption/AdoptionData.jsx';
import AdoptionReport from './adoption/views/AdoptionReport.jsx';
import Overview from './adoption/views/Overview.jsx';
import Companies from './adoption/views/Companies.jsx';
import CompanyDetail from './adoption/views/CompanyDetail.jsx';
import PromptCategories from './adoption/views/PromptCategories.jsx';
import Metrics from './adoption/views/Metrics.jsx';
import Trends from './adoption/views/Trends.jsx';
import './adoption/adoption.css';

const TABS = [
  ['adoption', 'Adoption'],
  ['usage', 'Usage & loyalty'],
  ['companies', 'Companies'],
  ['prompts', 'Prompt categories'],
  ['metrics', 'Metrics'],
  ['trends', 'Trends'],
];

function GlobalReport() {
  const { events, aliases } = useData();
  return <AdoptionReport events={events} aliases={aliases} scope="global" />;
}

function Inner() {
  const {
    loading, error, events, mergeMerck, setMergeMerck, days, setDays, selectedCompany,
  } = useData();
  const [tab, setTab] = useState('adoption');

  return (
    <div className="adopt-root">
      <div className="adopt-controls">
        <div className="adopt-subtabs">
          {TABS.map(([k, l]) => (
            <button key={k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>{l}</button>
          ))}
        </div>
        <label className="adopt-chk">
          <input type="checkbox" checked={mergeMerck} onChange={(e) => setMergeMerck(e.target.checked)} /> Merge EMD/Merck
        </label>
        <label className="adopt-days">Last
          <input type="number" min="1" max="365" value={days}
            onChange={(e) => setDays(Math.max(1, Math.min(365, parseInt(e.target.value, 10) || 1)))} /> days
        </label>
      </div>

      {loading && <div className="status">Loading usage from /api/usage…</div>}
      {error && (
        <div className="status err">
          Error loading usage: {error}. The backend must return per-event rows from
          <code> /api/usage/fetch?raw_events=true</code> (or expose a Graylog-backed
          <code> /api/usage/events</code>) for these analytics.
        </div>
      )}
      {!loading && !error && !events.length && <div className="status">No external usage in the selected window.</div>}

      {!loading && !error && events.length > 0 && (
        <div className="adopt-view">
          {tab === 'adoption' && <GlobalReport />}
          {tab === 'usage' && <Overview />}
          {tab === 'companies' && (selectedCompany ? <CompanyDetail /> : <Companies />)}
          {tab === 'prompts' && <PromptCategories />}
          {tab === 'metrics' && <Metrics />}
          {tab === 'trends' && <Trends />}
        </div>
      )}
    </div>
  );
}

const AdoptionAnalytics = ({ authData }) => (
  <AdoptionDataProvider authData={authData}><Inner /></AdoptionDataProvider>
);

export default AdoptionAnalytics;
